import { useEffect, useState } from "react";
import { toast } from "sonner";
import { authChangePassword, authChangeEmail, pendingEmailChange, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import EmailChangePending from "./EmailChangePending";
import { TInput, Btn, Card } from "./ui";

const errMsg = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

export default function ChangePasswordCard() {
  const { user, refresh } = useAuth();
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [em, setEm] = useState({ password: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState({ pending: false });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const setE = (k) => (v) => setEm((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    pendingEmailChange().then(setPending).catch(() => {});
  }, []);

  const submit = async () => {
    if (!f.current || !f.next || !f.confirm) { toast.error("Fill in every field."); return; }
    if (f.next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (f.next !== f.confirm) { toast.error("New passwords do not match."); return; }
    setBusy(true);
    try {
      await authChangePassword({ currentPassword: f.current, newPassword: f.next });
      toast.success("Password changed. Use it on your next sign-in.");
      setF({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const submitEmail = async () => {
    if (!em.password || !em.email) { toast.error("Enter your current password and the new email."); return; }
    setBusy(true);
    try {
      const r = await authChangeEmail({ currentPassword: em.password, newEmail: em.email });
      setPending(r);
      toast.success(`Confirmation link sent to ${r.newEmail}. The change applies once you confirm it there.`, { duration: 8000 });
      setEm({ password: "", email: "" });
    } catch (e) { toast.error(errMsg(e), { duration: 12000 }); }
    setBusy(false);
  };

  return (
    <Card className="mt-6" data-testid="security-card">
      <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-1">ACCOUNT SECURITY — CHANGE PASSWORD</p>
      <p className="font-mono text-[8px] tracking-[0.15em] text-white/30 mb-5 leading-relaxed">
        STORED AS A BCRYPT HASH — NEVER DISPLAYED OR LOGGED. YOUR NEW PASSWORD SURVIVES RESTARTS AND REDEPLOYMENTS.
      </p>
      <div className="grid md:grid-cols-3 gap-4">
        <TInput label="CURRENT PASSWORD" type="password" value={f.current} onChange={set("current")} testid="security-current-password" />
        <TInput label="NEW PASSWORD (MIN 8 CHARACTERS)" type="password" value={f.next} onChange={set("next")} testid="security-new-password" />
        <TInput label="CONFIRM NEW PASSWORD" type="password" value={f.confirm} onChange={set("confirm")} testid="security-confirm-password" />
      </div>
      <div className="mt-5">
        <Btn onClick={submit} disabled={busy} testid="security-change-password-save">CHANGE PASSWORD</Btn>
      </div>

      <div className="border-t border-white/10 mt-8 pt-6">
        <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-1">SIGN-IN EMAIL</p>
        <p className="font-mono text-[8px] tracking-[0.15em] text-white/30 mb-5 leading-relaxed">
          CURRENT: <span className="text-white/60" data-testid="security-current-email">{(user?.email || "").toUpperCase()}</span> — A CONFIRMATION LINK IS SENT TO THE NEW ADDRESS AND THE CHANGE APPLIES ONLY AFTER YOU CONFIRM IT. A SECURITY NOTICE GOES TO YOUR PREVIOUS ADDRESS. RESET LINKS ARE SENT HERE, SO USE A MAILBOX YOU CONTROL.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <TInput label="CURRENT PASSWORD" type="password" value={em.password} onChange={setE("password")} testid="security-email-password" />
          <TInput label="NEW EMAIL ADDRESS" type="email" value={em.email} onChange={setE("email")} testid="security-new-email" />
        </div>
        <div className="mt-5">
          <Btn onClick={submitEmail} disabled={busy} testid="security-change-email-save">CHANGE EMAIL</Btn>
        </div>
        {pending.pending && <EmailChangePending pending={pending} onChange={setPending} />}
      </div>
    </Card>
  );
}
